import { CONST } from "../CONST";
import { MinigameScene } from "./generic/MinigameScene";
import { Key, KeyState } from "../components/Instruments/Key";
import { Character } from "../components/Instruments/Character";
import BackButton from "../components/BackButton";
import TryAgainButton from "../components/TryAgainButton";
import ContinueButton from "../components/ContinueButton";
import { HeartDisplay } from "../components/Instruments/HeartDisplay";
import HomeButton from "../components/HomeButton";

export enum GameState {
    Playing = 1,
    Listening,
    Transitioning
}

export class InstrumentScene extends MinigameScene {

    private readonly minigameConfig = {
        lives: 3,
        startDifficulty: 2,
        notesPerDifficulty: 1,
        delayBeforeListeningStart: 2000, //how much time we wait for sth to start
        delayBeforePlayingStart: 1000, //how much time we wait for sth to start
        noteLength: {min: 500, max: 1250, step: 250},
    }

    private keysConfig = [
        {key: 1, x: 205, y: 305, darkImage: CONST.INSTRUMENT.BAR_DARK_1, whiteImage: CONST.INSTRUMENT.BAR_WHITE_1, audio: CONST.AUDIO.INSTRUMENT_1},
        {key: 2, x: 278, y: 314, darkImage: CONST.INSTRUMENT.BAR_DARK_2, whiteImage: CONST.INSTRUMENT.BAR_WHITE_2, audio: CONST.AUDIO.INSTRUMENT_2},
        {key: 3, x: 360, y: 320, darkImage: CONST.INSTRUMENT.BAR_DARK_3, whiteImage: CONST.INSTRUMENT.BAR_WHITE_3, audio: CONST.AUDIO.INSTRUMENT_3},
        {key: 4, x: 433, y: 332, darkImage: CONST.INSTRUMENT.BAR_DARK_4, whiteImage: CONST.INSTRUMENT.BAR_WHITE_4, audio: CONST.AUDIO.INSTRUMENT_4},
        {key: 5, x: 500, y: 340, darkImage: CONST.INSTRUMENT.BAR_DARK_5, whiteImage: CONST.INSTRUMENT.BAR_WHITE_5, audio: CONST.AUDIO.INSTRUMENT_5},
        {key: 6, x: 575, y: 348, darkImage: CONST.INSTRUMENT.BAR_DARK_6, whiteImage: CONST.INSTRUMENT.BAR_WHITE_6, audio: CONST.AUDIO.INSTRUMENT_6},
        {key: 7, x: 642, y: 358, darkImage: CONST.INSTRUMENT.BAR_DARK_7, whiteImage: CONST.INSTRUMENT.BAR_WHITE_7, audio: CONST.AUDIO.INSTRUMENT_7},
    ];

    private isRestart = false;

    //logic vars
    private endScoreDisplay !: Phaser.GameObjects.Text;
    private highscoreImage !: Phaser.GameObjects.Image;


    private keys: Key[] = [];
    private character !: Character;
    private state = GameState.Listening;
    private difficulty = 1;
    private currentSong: {key: number, time:number}[] = [];
    private currentSongIndex = -1;
    private nextNoteTime = 0;

    private currentScore: number = 0;
    private currentLives !: number;

    private heartDisplay !: HeartDisplay;
    private homeButton !: HomeButton;

    constructor(){
        super ({
            sceneName: CONST.SCENES.INSTRUMENT,
            duration: 999999999,
            showCountdown: 0,
            showHomeButton: false,
            timeToStart: 1000,
            startScreenColor: 0xCADA40,
            startText: "LISTEN\nAND REPEAT!",
            startTextYOrigin: 0.45,
            startTextColor: 0x323232,
            endScreen: CONST.INSTRUMENT.END,
            showSkipButton: false,
            nextScene: CONST.SCENES.BIRTH,
            endButtons: {
                normalNoSkip: {x: -426.5, y: 386, scale: 1.30},
                normalWithSkip: {x: 283, y: 386, scale: 1.30},
                skip: {x: 570, y: 386, scale: 1.30}
            },
            isArcadeMinigame: true,
          })
    }

    _init(){
        //Reset all class variables that change during scene here (including arrays that initialize in create!)
        this.keys = [];

        this.state = GameState.Listening;

        this.currentSong = [];
        this.currentSongIndex = -1;
        this.nextNoteTime = 0;

        this.currentLives = this.minigameConfig.lives;
        this.currentScore = 0;

        this.difficulty = this.minigameConfig.startDifficulty;
        this.currentLives = this.minigameConfig.lives;

        this.isRestart = this.checkForRestart();
    }

    private checkForRestart(): boolean{
        let key = this.sceneName + "restart";
        let restartInfo = this.storagePlugin.getItem(key, "");
        if (restartInfo){
            let parsed = JSON.parse(restartInfo);
            this.difficulty = parsed.difficulty;
            this.currentScore = parsed.score;
            this.currentSong = parsed.song;

            this.storagePlugin.removeItem(key);
            return true;
        }

        return false;
    }

    _preload(){
    }

    _create(){

       this.createBackground();
       this.createCharacter();
       this.createKeys();

        this.pauseMusic(true);

        this.heartDisplay = new HeartDisplay(this, this.currentLives);

        this.homeButton = new HomeButton(this, 40, this.renderer.height - 40).setDepth(30).setScrollFactor(0, 0, true).setScale(0.75);
        this.homeButton.addExitEvent(() => {
            this.pauseMusic(false);
        });
        
        this.startListeningPhase(true);
    }

    onGameStarted(){
    }

    createBackground() {
        this.add.image(0, 0, CONST.INSTRUMENT.BACKGROUND).setOrigin(0, 0).setDepth(0).setScale(1.05,1);
        this.add.image(0, 480, CONST.INSTRUMENT.TABLE).setOrigin(0, 1).setDepth(10);
    }

    createCharacter() {
        this.character = new Character(this, GameState.Playing);
    }

    createKeys() {
        this.keysConfig.forEach(config => {
            let key = new Key(this, config, KeyState.Playing);
            key.clickCallback = () => {
                this.handleKeyClick(config.key);
            };

            this.keys.push(key);
        });
    }

    startListeningPhase(firstTime: boolean = false) {
        if (!firstTime){
            this.sound.play(CONST.AUDIO.WIN, {
                volume: 0.2,
                delay: 0.75,
              });
        }

        this.updateGameState(GameState.Transitioning);

        this.time.addEvent({
            delay: this.minigameConfig.delayBeforeListeningStart,
            callback: () => {
                //Starts listening phase
                this.updateGameState(GameState.Listening);
                
                if (!firstTime){
                    this.difficulty += 1;
                }
                this.currentSongIndex = -1;
        
                this.updateCurrentSong()
            },
            callbackScope: this
        })
    }

    startPlayingPhase() {
        this.updateGameState(GameState.Transitioning);

        this.time.addEvent({
            delay: this.minigameConfig.delayBeforePlayingStart,
            callback: () => {
                this.updateGameState(GameState.Playing);
                this.currentSongIndex = 0;
            },
            callbackScope: this
        })
        
    }

    updateCurrentSong() {
        let notesToPlay = this.difficulty * this.minigameConfig.notesPerDifficulty;
        for (let i = this.currentSong.length; i < notesToPlay; i++){
            this.currentSong.push(this.getRandomNote());
        }
    }

    getRandomNote() {
        //get random key from keyconfig, add random duration
        var note = this.keysConfig[this.getRandomInt(0, this.keysConfig.length-1)];

        var maxTimeSteps = Math.round((this.minigameConfig.noteLength.max - this.minigameConfig.noteLength.min)/this.minigameConfig.noteLength.step);
        var noteLen = this.minigameConfig.noteLength.min + this.getRandomInt(0, maxTimeSteps)* this.minigameConfig.noteLength.step;

        return {key: note.key, time: noteLen};
    }

    updateGameState(newState: GameState) {
        this.state = newState;
        this.keys.forEach(key => {key.updateState(newState)})
        this.character.updateState(newState);
    }

    //Triggers when a key is clicked while in play mode
    handleKeyClick(keyCode: number) {
        this.character.animateNote({key: keyCode, time: 500});

        let expectedNote = this.currentSong[this.currentSongIndex];

        //Check for mistake
        if (keyCode != expectedNote.key){
            this.handleWrongNote(keyCode);
        } else {
            this.handleCorrectNote();
        }        
    }

    private handleCorrectNote(){
        this.currentScore++;
        this.currentSongIndex++;
        //Transition out of playing phase if the whole song was played already
        if (this.currentSongIndex >= this.currentSong.length){
            this.startListeningPhase();
        }
    }

    private handleWrongNote(note: number){
        this.currentLives--;
        this.heartDisplay.updateHearts(this.currentLives);


        this.keys.forEach((key) => {
            if (key.key == note){
                key.displayMistake();
            }
        });

        if (this.currentLives <= 0){
            this.triggerLose();
        } else {
            this.startListeningPhase(true);
        }       
    }

    _update(time: number, delta: number){
        this.updateListeningPhase(time);
    }

    updateListeningPhase(time: number){
        if (this.state != GameState.Listening) return;
        
        //Transition out of listening phase if the whole song was played already
        if (this.currentSongIndex >= this.currentSong.length){
            this.startPlayingPhase();
            return;
        }

        //Play the song if we are in listening phase
        if (time > this.nextNoteTime){
            this.playNextNote(time);
        }
    }

    //Plays the next note of the song in listening phase
    playNextNote(startTime: number) {
        this.currentSongIndex++;
        if (this.currentSongIndex >= this.currentSong.length) return;

        let note = this.currentSong[this.currentSongIndex];
        this.nextNoteTime = startTime + note.time;
        
        this.keys.forEach((key) => {
            if (key.key == note.key){
                key.simulateClick();
            }
        })
    }

    _timeout(){
    }

    _afterLoss(){
        this.pauseMusic(false);
    }

    _afterWin(){
    }

    private triggerLose(){
        this.homeButton.setVisible(false);

        setTimeout( () => {
            this.sound.play(CONST.AUDIO.LOSE);
        }, 750);

        this.saveAndDisplayScore();
        this.updateGameState(GameState.Transitioning);

        this.loseGame(2000);
    }

    private triggerWin(){
        //trigger win after 1 second and load next scene
        this.winGame(1000);
    }

    private saveAndDisplayScore(){

        var score = this.currentScore;
        var oldHighscore = this.getHighscore();
        var isNewHighscore = score > oldHighscore;

        this.displayScore(score, isNewHighscore, oldHighscore);

        if (isNewHighscore){
            this.setHighscore(score);
        }

        this.sendFinalScoreAnalyticsEvent(score);
    }

    private displayScore(score: number, isHighscore: boolean, oldHighscore: number){
        this.endScoreDisplay.text = score.toString();
        let scoreLength =  this.endScoreDisplay.text.length;

        if(scoreLength == 1){
            this.endScoreDisplay.x = 241;
            this.highscoreImage.x = 272;
            this.highscoreImage.y = 320;
        }
        if(scoreLength == 2){
            this.endScoreDisplay.x = 230;
            this.highscoreImage.x = 272;
            this.highscoreImage.y = 319;
        } 
        if(scoreLength >= 3){
            this.endScoreDisplay.x = 222;
            this.highscoreImage.x = 277;
            this.highscoreImage.y = 320;
        }

        this.highscoreImage.setAlpha(isHighscore ? 1 : 0);
    }

    private pauseMusic(pause: boolean){
        this.registry.get(CONST.SCENES.SOUNDTRACK).pauseMusic(pause);
    }

    protected getCustomLoseUIElements() : object[] {
        this.highscoreImage = this.add.image(277, 320, CONST.INSTRUMENT.NEW_BEST).setDepth(3);

        this.endScoreDisplay = this.add.text(224, 318, "123",
        {fontFamily: 'bloggerSans', fontSize: 35, color: "#EB5166", align: 'center'})
        .setDepth(2);

        if (this.isRestart || this.registry.get(CONST.REGISTRY.ADBLOCKENABLED)){
            //Without continue button
            var backButton2 = new BackButton(CONST.SCENES.MENU, 150, 402, this, CONST.INSTRUMENT.BACK_2).setScale(1.1);
            var tryAgainButton2 = new TryAgainButton(299, 391, this, CONST.INSTRUMENT.TRY_AGAIN_2).setScale(1.1).setDepth(2);
            return [backButton2, tryAgainButton2,  this.endScoreDisplay, this.highscoreImage];

        } else {
            //With continue button
            var backButton = new BackButton(CONST.SCENES.MENU, 235, 439, this, CONST.INSTRUMENT.BACK).setScale(1.1);
            var continueButton = new ContinueButton(304, 389, this, CONST.INSTRUMENT.CONTINUE, () => {return this.getContinueData()}).setScale(1.1);
            var tryAgainButton = new TryAgainButton(153, 397, this, CONST.INSTRUMENT.TRY_AGAIN).setDepth(2);
            return [backButton, continueButton, tryAgainButton,  this.endScoreDisplay, this.highscoreImage];
        }
    }

    private getContinueData(){
        return {difficulty: this.difficulty,
                score: this.currentScore,
                song: this.currentSong};
    }
}
